/**
 * Object signing and verification for Amorphous
 */
import * as ed from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import { base58 } from './base58.js';
import type { KeyPair } from './keys.js';
import type { DAGObject, ContentTypes } from '../dag/types.js';
import { getSigningData, computeHash, verifyHash } from '../dag/object.js';

// Required for ed25519 in browser/node
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m));

/**
 * Sign a DAG object
 */
export function signObject<T extends ContentTypes>(
  obj: Omit<DAGObject<T>, 'signature'>,
  keyPair: KeyPair
): DAGObject<T> {
  const signingData = getSigningData(obj);
  const signatureBytes = ed.sign(signingData, keyPair.privateKey);
  const signature = base58.encode(signatureBytes);

  return {
    ...obj,
    signature,
  };
}

/**
 * Verify a DAG object's signature and hash integrity
 */
export function verifySignature(obj: DAGObject): boolean {
  try {
    // First verify the hash matches the content
    if (!verifyHash(obj)) {
      return false;
    }

    // Then verify the signature matches the hash
    const signingData = getSigningData(obj);
    const signatureBytes = base58.decode(obj.signature);
    const publicKeyBytes = base58.decode(obj.creator);

    return ed.verify(signatureBytes, signingData, publicKeyBytes);
  } catch {
    return false;
  }
}

/**
 * Create and sign a new DAG object
 */
export function createSignedObject<T extends ContentTypes>(
  type: DAGObject['type'],
  content: T,
  keyPair: KeyPair,
  parents: string[] = []
): DAGObject<T> {
  const unsignedWithoutHash = {
    type,
    creator: keyPair.publicKeyBase58,
    timestamp: Date.now(),
    parents,
    content,
  };

  const hash = computeHash(unsignedWithoutHash);
  const unsigned = { ...unsignedWithoutHash, hash };

  return signObject(unsigned, keyPair);
}
